//
// # MoveWireSegment
//
// Click-and-drag a single schematic wire segment perpendicular to its axis
// (issue #2032). Horizontal wires slide vertically only; vertical wires slide
// horizontally only. The segment's two endpoints move by the same snapped
// delta, so the wire stays axis-aligned.
//
// Adjacent perpendicular wire segments that share an endpoint with the
// dragged wire stretch along their own axis so corners stay connected:
// only the "near" endpoint (the one shared with the dragged wire) follows;
// the far endpoint stays put. Adjacent **collinear** wires would have to
// bend to follow, which would violate the Manhattan-only constraint
// (#1879) — those are left alone in this commit and tracked as the
// detachment edge case. Pin attachments are likewise left to a later
// commit (the stub-wire insertion behavior described in the proposal).
//
// Multi-select drag (slide N selected wires at once) hooks into
// MoveSelection in a follow-up commit on this branch.
//
// Diagonal wires are out of scope per #1879; MoveWireSegment.enter
// returns null for them and the click falls back to plain select.
//

import { Point, graphics, model } from "GenAlphaModel";
import * as changes from "DrawCore/src/changes";
import type { InteractionHost } from "../interaction_host";
import { ModeHandler } from "./base";
import { DragThreshold } from "./axis_constrain";
import type { Schematic } from "../content/schematic/schematic";
import type { WireSegment } from "DrawCore/src/content/schematic/wire";

// Axis along which the segment runs. The drag is constrained to the
// PERPENDICULAR axis (a horizontal wire slides vertically).
export type WireAxis = "horizontal" | "vertical";

// Classify a wire segment by its axis based on its two endpoints. Returns
// null for diagonal or zero-length segments (not draggable here).
export function axisOf(start: Point, end: Point): WireAxis | null {
  if (start.y === end.y && start.x !== end.x) return "horizontal";
  if (start.x === end.x && start.y !== end.y) return "vertical";
  return null;
}

// Given a grid-snapped raw mouse delta and the dragged wire's axis,
// return only the component perpendicular to that axis. Horizontal wires
// produce a y-only delta; vertical wires produce an x-only delta. This is
// what keeps the slid wire axis-aligned regardless of cursor wander.
export function perpendicularComponent(
  snappedDelta: Point,
  axis: WireAxis
): Point {
  return {
    x: axis === "vertical" ? snappedDelta.x : 0,
    y: axis === "horizontal" ? snappedDelta.y : 0,
  };
}

// One adjacent perpendicular wire that needs to stretch along its own axis
// to follow the moved wire's endpoint. `nearIndex` is which endpoint of
// the adjacent wire (0 or 1) touches the dragged wire.
interface AdjacentWire {
  readonly segment: WireSegment;
  readonly origEndpoints: [Point, Point];
  readonly origLabels: Array<graphics.Text>;
  readonly nearIndex: 0 | 1;
}

function collectPerpendicularAdjacents(
  schematic: Schematic,
  dragged: WireSegment,
  draggedAxis: WireAxis
): AdjacentWire[] {
  const out: AdjacentWire[] = [];
  const [origA, origB] = dragged.data.endpoints;
  for (const w of schematic.wires.all()) {
    if (w === dragged) continue;
    const otherAxis = axisOf(w.data.endpoints[0], w.data.endpoints[1]);
    if (otherAxis === null) continue; // skip diagonal neighbors
    if (otherAxis === draggedAxis) continue; // skip collinear; bending forbidden
    // Find which endpoint of `w` touches a dragged endpoint, if any.
    const e0 = w.data.endpoints[0];
    const e1 = w.data.endpoints[1];
    let nearIndex: 0 | 1 | null = null;
    if (Point.eq(e0, origA) || Point.eq(e0, origB)) nearIndex = 0;
    else if (Point.eq(e1, origA) || Point.eq(e1, origB)) nearIndex = 1;
    if (nearIndex === null) continue;
    out.push({
      segment: w,
      origEndpoints: [Point.clone(e0), Point.clone(e1)],
      origLabels: model.schematic.cloneWireLabels(w.data.labels),
      nearIndex,
    });
  }
  return out;
}

// How the slide was initiated. Mouse-drag commits on mouseup; keyboard
// (G key on a single selected wire) commits on LMB and cancels on RMB.
type SlideTrigger = "drag" | "keyboard";

export class MoveWireSegment extends ModeHandler {
  override movesGeometryLive = true;

  private readonly editor: InteractionHost;
  private readonly origEndpoints: [Point, Point];
  private readonly origLabels: Array<graphics.Text>;
  private readonly axis: WireAxis;
  private readonly initialMousePos: Point;
  private readonly adjacents: AdjacentWire[];
  // Suppresses movement until the cursor leaves a small pixel radius around the
  // press. A mouse-drag arms it; the keyboard grab tracks the cursor immediately.
  private readonly dragThreshold: DragThreshold;

  private constructor(
    private readonly schematic: Schematic,
    private readonly segment: WireSegment,
    axis: WireAxis,
    initialMousePos: Point,
    adjacents: AdjacentWire[],
    private readonly trigger: SlideTrigger
  ) {
    super();
    this.editor = schematic.editor;
    this.axis = axis;
    this.initialMousePos = initialMousePos;
    this.adjacents = adjacents;
    this.dragThreshold =
      trigger === "drag"
        ? new DragThreshold(Point.clone(this.editor.uiState.mousePos.page))
        : DragThreshold.passthrough();
    this.origEndpoints = [
      Point.clone(segment.data.endpoints[0]),
      Point.clone(segment.data.endpoints[1]),
    ];
    this.origLabels = model.schematic.cloneWireLabels(segment.data.labels);
    const slideAxisLabel =
      axis === "horizontal" ? "vertically" : "horizontally";
    this.editor.overlays.setStatusMessage(
      trigger === "keyboard"
        ? `Grab wire (${slideAxisLabel}): left-click to confirm, right-click to cancel`
        : `Drag: sliding wire ${slideAxisLabel}`,
      "move-wire-segment"
    );
  }

  // Enter this mode for a single wire segment. Returns null (no-op) for
  // diagonal/zero-length wires so the caller can fall back to plain select.
  static enter(schematic: Schematic, segment: WireSegment): unknown {
    return MoveWireSegment.enterWith(schematic, segment, "drag");
  }

  // Keyboard-initiated entry (G key with a single selected wire).
  static enterKeyboard(schematic: Schematic, segment: WireSegment): unknown {
    return MoveWireSegment.enterWith(schematic, segment, "keyboard");
  }

  private static enterWith(
    schematic: Schematic,
    segment: WireSegment,
    trigger: SlideTrigger
  ): unknown {
    const editor = schematic.editor;
    const [start, end] = segment.data.endpoints;
    const axis = axisOf(start, end);
    if (axis === null) {
      editor.overlays.setStatusMessage(
        "Wire is not axis-aligned — cannot slide",
        "move-wire-segment"
      );
      return null;
    }
    const adjacents = collectPerpendicularAdjacents(schematic, segment, axis);
    const { mousePos } = editor.uiState;
    return editor.setMode(
      new MoveWireSegment(
        schematic,
        segment,
        axis,
        Point.clone(mousePos.sch),
        adjacents,
        trigger
      )
    );
  }

  override handleMouseMove = (): void => {
    const {
      editor,
      segment,
      axis,
      initialMousePos,
      origEndpoints,
      origLabels,
      adjacents,
    } = this;
    const { mousePos } = editor.uiState;

    // Ignore sub-threshold cursor motion so a plain click doesn't slide the wire.
    if (!this.dragThreshold.begun(mousePos.page)) return;

    // Raw delta in both axes, snap to grid, then keep only the
    // perpendicular component (extracted as a pure helper for testing).
    const rawDelta = Point.sub(mousePos.sch, initialMousePos);
    const snappedDelta = editor.grid.nearestPoint(rawDelta);
    const { x: dx, y: dy } = perpendicularComponent(snappedDelta, axis);

    const [oa, ob] = origEndpoints;
    const newA: Point = { x: oa.x + dx, y: oa.y + dy };
    const newB: Point = { x: ob.x + dx, y: ob.y + dy };
    // The dragged segment translates rigidly, so its labels re-center on the
    // new geometry.
    segment.update({
      endpoints: [newA, newB],
      labels: model.schematic.recenterLabels(origLabels, newA, newB),
    });

    // Stretch each adjacent perpendicular wire: move only its near
    // endpoint to match the dragged wire's matching endpoint; far stays put.
    for (const adj of adjacents) {
      const origNear = adj.origEndpoints[adj.nearIndex];
      const newNear = Point.eq(origNear, oa) ? newA : newB;
      const farIdx = adj.nearIndex === 0 ? 1 : 0;
      const far = adj.origEndpoints[farIdx];
      const endpoints: [Point, Point] =
        adj.nearIndex === 0
          ? [{ x: newNear.x, y: newNear.y }, Point.clone(far)]
          : [Point.clone(far), { x: newNear.x, y: newNear.y }];
      adj.segment.update({
        endpoints,
        labels: model.schematic.recenterLabels(
          adj.origLabels,
          endpoints[0],
          endpoints[1]
        ),
      });
    }

    editor.requestRedraw();
  };

  override handleMouseUp = (): void => {
    if (this.trigger === "drag") {
      this.commit();
    }
  };

  // Keyboard-initiated only: LMB confirms, any other button cancels.
  override handleMouseDown = (e: MouseEvent): unknown => {
    if (this.trigger !== "keyboard") return null;
    if (e.button === 0) {
      this.commit();
    } else {
      this.abort();
      this.editor.requestRedraw();
    }
    return null;
  };

  private commit = (): void => {
    const { editor, schematic, segment, origEndpoints, adjacents } = this;
    const singleChanges: changes.Single<WireSegment>[] = [];

    const newPrimary: [Point, Point] = [
      Point.clone(segment.data.endpoints[0]),
      Point.clone(segment.data.endpoints[1]),
    ];
    const primaryMoved =
      !Point.eq(origEndpoints[0], newPrimary[0]) ||
      !Point.eq(origEndpoints[1], newPrimary[1]);
    if (primaryMoved) {
      singleChanges.push(
        changes.update({
          target: segment,
          from: { endpoints: origEndpoints, labels: this.origLabels },
          to: {
            endpoints: newPrimary,
            labels: model.schematic.recenterLabels(
              this.origLabels,
              newPrimary[0],
              newPrimary[1]
            ),
          },
        })
      );
    }

    // For each adjacent: log Update if it moved, OR Remove if the drag
    // collapsed it to zero length (the near endpoint landed on the far
    // endpoint). Zero-length wires are useless visual clutter and confuse
    // later interactions — drop them.
    for (const adj of adjacents) {
      const newEndpoints: [Point, Point] = [
        Point.clone(adj.segment.data.endpoints[0]),
        Point.clone(adj.segment.data.endpoints[1]),
      ];
      const collapsed = Point.eq(newEndpoints[0], newEndpoints[1]);
      const adjMoved =
        !Point.eq(adj.origEndpoints[0], newEndpoints[0]) ||
        !Point.eq(adj.origEndpoints[1], newEndpoints[1]);

      if (collapsed) {
        // Reset the segment to its original endpoints (and labels) before
        // logging the Remove so an undo restores the wire at its pre-drag
        // location (not as a degenerate zero-length sliver).
        adj.segment.update({
          endpoints: [
            Point.clone(adj.origEndpoints[0]),
            Point.clone(adj.origEndpoints[1]),
          ],
          labels: model.schematic.cloneWireLabels(adj.origLabels),
        });
        singleChanges.push(changes.remove(adj.segment));
        schematic.wires.delete(adj.segment, false);
      } else if (adjMoved) {
        singleChanges.push(
          changes.update({
            target: adj.segment,
            from: { endpoints: adj.origEndpoints, labels: adj.origLabels },
            to: {
              endpoints: newEndpoints,
              labels: model.schematic.recenterLabels(
                adj.origLabels,
                newEndpoints[0],
                newEndpoints[1]
              ),
            },
          })
        );
      }
    }

    if (singleChanges.length === 1) {
      schematic.logChange(singleChanges[0]);
    } else if (singleChanges.length > 1) {
      const combo = changes.Combo.fromArray(singleChanges);
      if (combo.ok) schematic.logChange(combo.val);
    }

    // Refresh junction dots — wires moved, so old T-junction / 3-way
    // intersection markers may be stale.
    schematic.updateDots();
    editor.overlays.clearStatusMessage("move-wire-segment");
    editor.goUiIdle();
  };

  override abort = (): unknown => {
    const { editor, schematic, segment, origEndpoints, origLabels, adjacents } =
      this;
    segment.update({
      endpoints: [Point.clone(origEndpoints[0]), Point.clone(origEndpoints[1])],
      labels: model.schematic.cloneWireLabels(origLabels),
    });
    for (const adj of adjacents) {
      adj.segment.update({
        endpoints: [
          Point.clone(adj.origEndpoints[0]),
          Point.clone(adj.origEndpoints[1]),
        ],
        labels: model.schematic.cloneWireLabels(adj.origLabels),
      });
    }
    schematic.updateDots();
    editor.overlays.clearStatusMessage("move-wire-segment");
    editor.goUiIdle();
    return null;
  };
}
